<template>
  <div>
    <SelectInputView v-if="dataType.inputType === 'select' && parameter.searchable" searchable :multiple="parameter.multiple" :max="parameter.multipleMax" :options="httpSelectInputOptions" :required="parameter.required" :errors="errors" :title="parameter.title" :value="value" @update:value="emit('update:value', $event)" @update:query="httpSelectInputQuery = $event">
      <template v-slot:option="{ option, preview }">
        <span v-if="/{{[^}]+}}/gi.test(option.id)" class="bg-secondary rounded-2 px-2 py-1" style="font-size: 0.85em">
          <OutputView :output-path="option.id.trim().replaceAll('{{', '').replaceAll('}}', '')" :trigger="trigger" :data-types="dataTypes"/>
        </span>
        <div v-else>
          <span v-if="preview" class="bg-secondary rounded-2 px-2 py-1" style="font-size: 0.85em">
          {{ option.title }}
          </span>
          <span v-else>
            {{ option.title }}
          </span>
        </div>
      </template>
    </SelectInputView>
    <SelectInputView v-else-if="dataType.inputType === 'select' && parameter.options" searchable :multiple="parameter.multiple" :max="parameter.multipleMax" :required="parameter.required" :errors="errors" :title="parameter.title" :value="value" :options="[...placeholders, ...parameter.options]" @update:value="emit('update:value', $event)">
      <template v-slot:option="{ option, preview }">
        <span v-if="/{{[^}]+}}/gi.test(option.id)" class="bg-secondary rounded-2 px-2 py-1" style="font-size: 0.85em">
          <OutputView :output-path="option.id.trim().replaceAll('{{', '').replaceAll('}}', '')" :trigger="trigger" :data-types="dataTypes"/>
        </span>
        <div v-else>
          <span v-if="preview" class="bg-secondary rounded-2 px-2 py-1" style="font-size: 0.85em">
            {{ option.title }}
          </span>
          <span v-else>
            {{ option.title }}
          </span>
        </div>
      </template>
    </SelectInputView>
    <PlaceholderableTextInputView v-else-if="dataType.inputType === 'text'" :placeholders="getPlaceholders(trigger.outputs, []).map(item => ({ key: item }))" :filter="({ query, item })  => item.key.toLowerCase().includes(query.toLowerCase()) || item.name.toLowerCase().includes(query.toLowerCase())" :max="parameter.max" :multiline="parameter.multiline" :required="parameter.required" :errors="errors" :title="parameter.title" :trigger="trigger" :data-types="dataTypes" :value="value" @update:value="emit('update:value', $event)">
      <template v-slot:placeholder="{ placeholder, preview }">
        <span v-if="preview" class="bg-primary rounded-3" style="padding: 0.05em 0.25em; margin: 0.04em 0.02em; font-size: 0.85em">
          <OutputView :trigger="trigger" :data-types="dataTypes" :output-path="placeholder.key"/>
        </span>
        <span v-else>
          <OutputView :trigger="trigger" :data-types="dataTypes" :output-path="placeholder.key"/>
        </span>
      </template>
    </PlaceholderableTextInputView>
    <DatePickerView v-else-if="dataType.inputType === 'datetime'" :required="parameter.required" :errors="errors" :title="parameter.title" time :min="parameter.min" :max="parameter.max" :value="value && typeof value === 'string' ? value : undefined" @update:value="emit('update:value', $event)">
      <template v-slot:target="{ toggle }">
        <ButtonView type="normal" color="secondary" @click="toggle">
          {{ value ? utilities.formatDatetime(value) : "" }}
        </ButtonView>
      </template>
    </DatePickerView>
    <SelectInputView v-else-if="dataType.inputType === 'checkbox'" :required="parameter.required" :errors="errors" :title="parameter.title" :value="value" @update:value="emit('update:value', $event)" :options="[{title: translate('yes'), id: true}, {title: translate('no'), id: false}]">
      <template v-slot:option="{ option }">
        {{ option.title }}
      </template>
    </SelectInputView>
    <DurationInputView v-else-if="dataType.inputType === 'duration'" :required="parameter.required" locale="en-US" :errors="errors" :title="parameter.title" :value="value" @update:value="emit('update:value', $event)"/>
  </div>
</template>

<script setup>
import {ButtonView, DatePickerView, utilities, SelectInputView, DurationInputView, PlaceholderableTextInputView} from "@niftybit/vue";
import OutputView from "@/systems/modules/automation/form/OutputView.vue";
import {computed, ref} from "vue";
import modelSearch from "@niftybit/vue/src/modelSearch";
import toasts from "@niftybit/vue/src/plugins/toasts/toasts";

const props = defineProps({
  communityId: String,
  trigger: Object,
  action: Object,
  parameter: Object,
  value: [Number, String, Boolean, Array],
  dataTypes: Array,
  errors: Array
});

const emit = defineEmits(["update:value"]);

const httpSelectInputQuery = ref("");
const httpSelectInputOptions = ref([]);
const httpSelectInputIdentifiers = computed(() => props.value ? (Array.isArray(props.value) ? props.value : [props.value]).filter(item => !/{{[^}]+}}/gi.test(item)) : undefined);


function getPlaceholdersOfType(outputs, path) {
  let list = [];
  outputs.forEach(output => {
    if (output.type.key === dataType.value.key) list.push({
      path: [...path, output.key].join("."),
      title: output.title
    });
    let children = props.dataTypes.find(item => item.key === output.type.key)?.outputs ?? [];
    if (children) list.push(...getPlaceholdersOfType(children, [...path, output.key]));
  })
  return list;
}

function getPlaceholders(outputs, path) {
  let list = [];
  outputs.forEach(output => {
    const outputDataType = props.dataTypes.find(item => item.key === output.type.key);
    if (outputDataType.placeholderable) list.push([...path, output.key].join("."));
    let children = outputDataType?.outputs ?? [];
    if (children) list.push(...getPlaceholders(children, [...path, output.key]));
  })
  return list;
}

const placeholders = computed(() => getPlaceholdersOfType(props.trigger.outputs, []).map(item => ({ id: "{{" + item.path + "}}", title: item.title })));

const dataType = computed(() => props.dataTypes.find(item => item.key === props.parameter.type.key));

if (dataType.value.inputType === "select" && props.parameter.searchable) {
  modelSearch.search(utilities.path('communities/@/modules/automation/actions/@/parameters/@/search', props.communityId, props.action.key, props.parameter.key), httpSelectInputQuery, httpSelectInputIdentifiers, ({ results, error }) => {
    if (results) {
      httpSelectInputOptions.value = httpSelectInputQuery.value.length > 0 ? placeholders.value.filter(item => item.id.toLowerCase().includes(httpSelectInputQuery.value) || item.title.toLowerCase().includes(httpSelectInputQuery.value)) : placeholders.value;
      httpSelectInputOptions.value = httpSelectInputOptions.value.concat(results);
    } else if (error) {
      toasts.create({
        color: "danger",
        content: error?.response?.data?.message ? error.response.data.message : error.message
      });
    }
  });
}

</script>